import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { deepMap } from 'utils/helpersReact';
import { getTabsCount as getTabsCountHelper } from 'components/Tabs/helpers';
import { isTabList, isTabPanel } from 'components/Tabs/helpers/typeChecker';

interface IControlledTabsProps {
    children: React.ReactElement,
    onSelect: (index: number) => void,
    selectedTab: number,
    tabPanelClassname: string,
    selectedTabPanelClassname: string,
    tabListClassname: string,
    tabClassname: string,
    selectedTabClassname: string,
    tabsContentWrapperClassname: string
}

const tabIdSeparator = "/id=";

const ControlledTabs: React.FC<IControlledTabsProps> = (props) => {
    const {
        children,
        onSelect,
        selectedTab,
        tabPanelClassname,
        selectedTabPanelClassname,
        tabClassname,
        selectedTabClassname,
        tabsContentWrapperClassname,
        tabListClassname
    } = props;

    const [tabsCount, setTabsCount] = useState<number>(0);
    const [tabIds, setTabIds] = useState<string[]>([]);

    useEffect(() => {
        const count = getTabsCountHelper(children);
        setTabsCount(count);
    },[]);

    const setSelectedHandler = (index: number) => {
        if(index < 0 || index >= tabsCount) return;
    
        onSelect(index);
    }

    const getTabIndexById = (id: string, separator: string): number => {
        return parseInt(id.split(separator)[1])
    }

    const handleClick = (e: React.MouseEvent<any>) => {
        const targetAttribute = (e.target as any).getAttribute("data-tab-type"); 
        if(targetAttribute && targetAttribute === "switch") {
            const index = getTabIndexById((e.target as HTMLButtonElement).id, tabIdSeparator);
            setSelectedHandler(index);
        }
    }

    const getChildren = () => {
        let index = 0,
            difference = getTabsCountHelper(children) - tabIds.length,
            tabIdsWithoutState: string[] = [];

        const id = uuidv4();

        for(let i = 0; i < difference; i++) {
            tabIdsWithoutState.push(`${id}${tabIdSeparator}${i}`);
            setTabIds(prev => {
                return [
                    ...prev,
                    `${id}${tabIdSeparator}${i}`
                ]
            })
        }
        
        return deepMap(children, (child) => {
            let result = child;

            if(!child.type.hasOwnProperty("tabsRole")) {
                return result;
            }

            if(typeof child.type === "string") {
                return result;
            }

            const tabIdsCondition = tabIds.length <= 0 ? tabIdsWithoutState : tabIds; 

            if(isTabPanel(child)) {
                const props = {
                    id: tabIdsCondition[index],
                    selected: selectedTab === index,
                    tabPanelClassname,
                    selectedTabPanelClassname
                }

                result = React.cloneElement(
                    child,
                    props
                )

                index++

            } else if(isTabList(child)) {
                let listIndex = 0;

                const tabListProps = {
                    children: deepMap(child.props.children, (tab) => {

                        const selected = selectedTab === listIndex;

                        const props = {
                            id: tabIdsCondition[listIndex],
                            selected,
                            tabClassname,
                            selectedTabClassname
                        }

                        listIndex++;

                        return React.cloneElement(
                            tab,
                            props
                        )
                        
                    }),
                    className: tabListClassname
                }

                result = React.cloneElement(
                    child,
                    tabListProps
                )
            }
            return result
        })
    }   

    return (
        <div 
            className={tabsContentWrapperClassname}
            onClick={handleClick}
            data-tabs-wrapper
        >
            {getChildren()}
        </div>
    )
}

export default ControlledTabs